import * as React from "react";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableOptions,
} from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  TextField,
} from "@mui/material";
import { CancelOutlined, CheckBoxOutlined } from "@mui/icons-material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import axios from "../../../api";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import dayjs from "dayjs";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { AxiosError } from "axios";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

const tableOptions: MUIDataTableOptions = {
  selectableRows: "none",
  selectableRowsHeader: false,
  elevation: 0,
  tableBodyHeight: "auto",
  responsive: "standard",
};

// Validation Schema
const validationSchema = Yup.object({
  spare_part_name: Yup.string().required("Spare part name is required"),
  quantity: Yup.number()
    .min(1, "Quantity must be at least 1")
    .required("Quantity is required"),
  price: Yup.number()
    .min(0, "Price must be positive")
    .required("Price is required"),
  sku: Yup.string().required("SKU is required"),
});

interface AssetTypeOption {
  asset_type_id: number;
  asset_type_name: string;
}

interface SpareParts {
  request_id: number;
  asset_type_id: number;
  asset_type_name?: string;
  quantity: number;
  spare_part_id: number;
  status: string;
  spare_part_name: string;
  custom_spare_name: string;
}

interface Request {
  request_id: number;
}

// Utility function to get status color
const getStatusColor = (
  status: string
):
  | "success"
  | "error"
  | "default"
  | "primary"
  | "secondary"
  | "info"
  | "warning" => {
  if (status === "Approved") return "success";
  if (status === "Rejected") return "error";
  return "default";
};

// Extracted Component: Spare Part Name Cell
interface SparePartNameCellProps {
  row: SpareParts | undefined;
}

const SparePartNameCell: React.FC<SparePartNameCellProps> = ({ row }) => {
  // Safety check for undefined row
  if (!row) {
    return <span>-</span>;
  }

  if (row.spare_part_name) {
    return <span>{row.spare_part_name}</span>;
  }

  if (row.custom_spare_name) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <span>{row.custom_spare_name}</span>
        <Chip
          label="New"
          size="small"
          color="primary"
          sx={{
            fontSize: "0.7rem",
            fontWeight: "bold",
            height: 20,
            borderRadius: "6px",
          }}
        />
      </Box>
    );
  }

  return <span>-</span>;
};

// Extracted Component: Action Buttons
interface ActionButtonsCellProps {
  row: SpareParts;
  onApprove: (row: SpareParts) => void;
  onReject: (requestId: number) => void;
}

const ActionButtonsCell: React.FC<ActionButtonsCellProps> = ({
  row,
  onApprove,
  onReject,
}) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Button
        variant="contained"
        color="primary"
        size="small"
        startIcon={<CheckBoxOutlined />}
        sx={{ textTransform: "none", fontWeight: "bold", whiteSpace: "nowrap" }}
        onClick={() => onApprove(row)}
      >
        Approve
      </Button>

      <Button
        variant="contained"
        color="error"
        size="small"
        startIcon={<CancelOutlined />}
        sx={{ textTransform: "none", fontWeight: "bold", whiteSpace: "nowrap" }}
        onClick={() => onReject(row.request_id)}
      >
        Reject
      </Button>
    </Box>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

// Extracted Component: Status Chip
interface StatusChipCellProps {
  status: string;
}

const StatusChipCell: React.FC<StatusChipCellProps> = ({ status }) => {
  const color = getStatusColor(status);

  return (
    <Chip
      label={status}
      color={color}
      sx={{
        fontWeight: "bold",
        textTransform: "capitalize",
        width: 100,
        textAlign: "center",
      }}
    />
  );
};

// Extracted Component: Actions Cell (combines status and buttons)
interface ActionsCellProps {
  row: SpareParts | undefined;
  onApprove: (row: SpareParts) => void;
  onReject: (requestId: number) => void;
}

const ActionsCell: React.FC<ActionsCellProps> = ({
  row,
  onApprove,
  onReject,
}) => {
  // Safety check for undefined row
  if (!row) {
    return null;
  }

  const hasStatus = row.status && row.status !== "Pending";

  if (hasStatus) {
    return <StatusChipCell status={row.status} />;
  }

  return (
    <ActionButtonsCell row={row} onApprove={onApprove} onReject={onReject} />
  );
};

// Extracted Component: Required Field Label
interface RequiredFieldLabelProps {
  label: string;
}

const RequiredFieldLabel: React.FC<RequiredFieldLabelProps> = ({ label }) => {
  return (
    <SoftTypography variant="caption" fontWeight="bold">
      {label} <span style={{ color: "red" }}>*</span>
    </SoftTypography>
  );
};

const SparePartRequest = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const { fetchError, fetchSuccess } = useSnackbar();
  const [rows, setRows] = React.useState<SpareParts[]>([]);
  const [assetTypes, setAssetTypes] = React.useState<AssetTypeOption[]>([]);
  const [assetTypeFilter, setAssetTypeFilter] = React.useState<AssetTypeOption | null>(null);
  const [openModal, setOpenModal] = React.useState(false);
  const [selectedRequest, setSelectedRequest] = React.useState<Request | null>(
    null
  );
  const [showAll, setShowAll] = React.useState(false);
  const [initialValues, setInitialValues] = React.useState({
    spare_part_name: "",
    price: 0,
    quantity: 1,
    asset_type_id: 0,
    sku: "",
  });

  const fetchRequests = React.useCallback(async (showAllRequests = false) => {
    try {
      const url = showAllRequests
        ? "/spare-part-request/all"
        : "/spare-part-request/pending";
      const response = await axios.get(url);
      setRows(response?.data?.requests || response?.data || []);
    } catch (error) {
      console.error("Failed to fetch subscriptions", error);
      throw error;
    }
  }, []);

  const fetchAssetTypes = React.useCallback(async () => {
    try {
      const res = await axios.get("/asset-types");
      const list = res.data?.list ?? [];
      setAssetTypes(
        list.map((a: AssetTypeOption) => ({
          asset_type_id: a.asset_type_id,
          asset_type_name: a.asset_type_name ?? "",
        }))
      );
    } catch (e) {
      console.error("Failed to fetch asset types", e);
    }
  }, []);

  React.useEffect(() => {
    fetchAssetTypes();
  }, [fetchAssetTypes]);

  const handleToggleRequests = () => {
    const newShowAll = !showAll;
    setShowAll(newShowAll);
    fetchRequests(newShowAll);
  };

  React.useEffect(() => {
    fetchRequests(showAll);
  }, [fetchRequests, showAll]);

  const filteredRows = React.useMemo(() => {
    if (!assetTypeFilter) return rows;
    return rows.filter((r) => r.asset_type_id === assetTypeFilter.asset_type_id);
  }, [rows, assetTypeFilter]);

  const handleStatusUpdate = async (id: number, status: string) => {
    try {
      await axios.put(`/spare-part-request/${id}/status`, {
        status,
        approved_by: user?.userId,
        remarks:
          status === "Approved" ? "Approved by admin" : "Rejected by admin",
      });
      fetchSuccess(`Request ${status} successfully`);
      fetchRequests();
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const msg =
          error.response.data?.error ||
          error.response.data?.message ||
          "Failed to update request";
        fetchError(msg);
      }
    }
  };

  const handleApprove = (row: SpareParts) => {
    const isNewSparePart = row.custom_spare_name && !row.spare_part_id;

    if (isNewSparePart) {
      setSelectedRequest(row);
      setInitialValues({
        spare_part_name: row.custom_spare_name || "",
        quantity: row.quantity || 1,
        price: 0,
        sku: "",
        asset_type_id: row.asset_type_id || 0,
      });
      setOpenModal(true);
    } else {
      handleStatusUpdate(row.request_id, "Approved");
    }
  };

  const handleReject = (requestId: number) => {
    handleStatusUpdate(requestId, "Rejected");
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const renderSparePartNameCell = (dataIndex: number) => {
    const row = filteredRows[dataIndex];
    if (!row) return <span>-</span>;
    return <SparePartNameCell row={row} />;
  };

  const renderActionsCell = (dataIndex: number) => {
    const row = filteredRows[dataIndex];
    if (!row) return <span>-</span>;
    return (
      <ActionsCell
        row={row}
        onApprove={handleApprove}
        onReject={handleReject}
      />
    );
  };

  const columns: MUIDataTableColumn[] = [
    { name: "request_id", label: "Request Id" },
    { name: "asset_type_name", label: "Asset Type" },
    {
      name: "spare_part_name",
      label: "Spare Part",
      options: {
        customBodyRenderLite: renderSparePartNameCell,
      },
    },
    { name: "requested_name", label: "Requested By" },
    { name: "quantity", label: "Quantity" },
    {
      name: "request_date",
      label: "Request Date",
      options: {
        customBodyRender: (value) => {
          if (!value) return "-";
          return dayjs(value).format("MMM DD, YYYY");
        },
      },
    },
    {
      name: "actions",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: renderActionsCell,
      },
    },
  ];

  const handleSubmit = async (
    values: {
      spare_part_name: string;
      price: number;
      quantity: number;
      asset_type_id: number;
      sku: string;
    },
    { setSubmitting }: any
  ) => {
    try {
      await axios.post(
        `/spare-part-request/${selectedRequest?.request_id}/add-part`,
        {
          spare_part_name: values.spare_part_name,
          price: values.price,
          quantity: values.quantity,
          asset_type_id: values.asset_type_id,
          sku: values.sku,
          client_id: user?.clientId,
        }
      );
      fetchSuccess("Spare part created and linked successfully");
      await handleStatusUpdate(selectedRequest?.request_id ?? 0, "Approved");
      setOpenModal(false);
      fetchRequests();
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(
          error.response?.data?.message || "Failed to create spare part"
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <Card sx={{ p: 3, m: "0 auto", maxWidth: "2000px" }}>
          <Box display="flex" flexWrap="wrap" alignItems="flex-end" gap={1.5} mb={2}>
            <FormControl sx={{ width: 200, minWidth: 200 }}>
              <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5 }}>
                Asset Type
              </SoftTypography>
              <Autocomplete
                options={assetTypes}
                getOptionLabel={(o) => o.asset_type_name}
                value={assetTypeFilter}
                onChange={(_, v) => setAssetTypeFilter(v)}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" size="small" placeholder="All" />
                )}
              />
            </FormControl>
            <Button
              variant="contained"
              color={showAll ? "secondary" : "primary"}
              onClick={handleToggleRequests}
            >
              {showAll ? "Show Pending Only" : "Show All Requests"}
            </Button>
          </Box>

          <SoftBox>
            <ThemeProvider theme={getMuiTheme}>
              <MUIDataTable
                title={"Items Request"}
                data={filteredRows}
                columns={columns}
                options={tableOptions}
              />
            </ThemeProvider>
          </SoftBox>
        </Card>
      </SoftBox>
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <SoftTypography variant="h5" fontWeight="bold">
            Create New Spare Part
          </SoftTypography>
        </DialogTitle>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ errors, touched, isSubmitting }) => (
            <Form>
              <DialogContent>
                <SoftBox>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <SoftBox mb={1}>
                        <RequiredFieldLabel label="Spare Part Name" />
                      </SoftBox>
                      <Field
                        name="spare_part_name"
                        as={TextField}
                        fullWidth
                        variant="outlined"
                        placeholder="Enter spare part name"
                        error={
                          !!errors.spare_part_name && touched.spare_part_name
                        }
                        helperText={
                          touched.spare_part_name && errors.spare_part_name
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SoftBox mb={1}>
                        <RequiredFieldLabel label="Quantity" />
                      </SoftBox>
                      <Field
                        name="quantity"
                        as={TextField}
                        type="number"
                        fullWidth
                        variant="outlined"
                        placeholder="Enter quantity"
                        error={!!errors.quantity && touched.quantity}
                        helperText={touched.quantity && errors.quantity}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SoftBox mb={1}>
                        <RequiredFieldLabel label="Price" />
                      </SoftBox>
                      <Field
                        name="price"
                        as={TextField}
                        type="number"
                        fullWidth
                        variant="outlined"
                        placeholder="Enter price"
                        error={!!errors.price && touched.price}
                        helperText={touched.price && errors.price}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <SoftBox mb={1}>
                        <RequiredFieldLabel label="SKU" />
                      </SoftBox>
                      <Field
                        name="sku"
                        as={TextField}
                        fullWidth
                        variant="outlined"
                        placeholder="Enter SKU"
                        error={!!errors.sku && touched.sku}
                        helperText={touched.sku && errors.sku}
                      />
                    </Grid>
                  </Grid>
                </SoftBox>
              </DialogContent>

              <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button
                  onClick={handleCloseModal}
                  variant="outlined"
                  color="secondary"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Save & Approve"}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </DashboardLayout>
  );
};

export default SparePartRequest;